// hooks/useFetchData.js
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export default function useFetchData(
  pageIndex,
  pageSize,
  queryKey,
  fetchData,
  filters, // memoized filters object
  setTotalCountHandler // dispatches totalCountChangedValue
) {
  const [data, setData] = useState({ data: [], meta: { totalItems: 0 } });
  const queryClient = useQueryClient();

  // Build a stable key array so React-Query only reruns when one of these truly changes
  // const key = useMemo(() => [queryKey, pageIndex, pageSize, filters], [queryKey, pageIndex, pageSize, filters]);

  // Use a *serialized* filters key so changes are detected even if the same object ref is reused
  const filtersKey = useMemo(() => JSON.stringify(filters ?? {}), [filters]);
  const key = useMemo(
    () => [queryKey, { pageIndex, pageSize, filtersKey }],
    [queryKey, pageIndex, pageSize, filtersKey]
  );

  const {
    data: resData,
    isSuccess,
    isFetching,
    ...rest
  } = useQuery(
    key,
    // () => fetchData(pageSize, pageIndex + 1, filters), {
    // keepPreviousData: true,
    // staleTime: Infinity,
    // onSuccess(res) {
    //   // 1) tell parent the new total
    //   setTotalCountHandler({ totalCountChangedValue: res.meta.totalItems });

    //   // 2) prefetch the *next* page if there is one
    //   const totalPages = Math.ceil(res.meta.totalItems / pageSize);
    //   if (pageIndex + 1 < totalPages) {
    //     const nextKey = [queryKey, pageIndex + 1, pageSize, filters];
    //     queryClient.prefetchQuery(nextKey, () => fetchData(pageSize, pageIndex + 2, filters));
    //   }
    // }

    () => fetchData(pageSize, pageIndex + 1, filters),
    {
      keepPreviousData: true,
      // avoid “sticky fresh” empty pages when filters change
      staleTime: 0,
      select(res) {
        // normalize the shape so downstream logic is stable
        const totalItems = Number(res?.meta?.totalItems ?? 0);
        const items = Array.isArray(res?.data) ? res.data : [];
        return { data: items, meta: { totalItems } };
      },
      onSuccess(res) {
        const totalItems = Number(res?.meta?.totalItems ?? 0);
        setTotalCountHandler({ totalCountChangedValue: totalItems });

        // prefetch next page if it exists (0-based -> 1-based API)
        const totalPages = Math.ceil(totalItems / pageSize);
        const nextPageIndex = pageIndex + 1; // 0-based
        const nextApiPage = nextPageIndex + 1; // 1-based
        if (nextApiPage <= totalPages) {
          const nextKey = [queryKey, { pageIndex: nextPageIndex, pageSize, filtersKey }];
          queryClient.prefetchQuery(nextKey, () => fetchData(pageSize, nextApiPage, filters));
        }
      }
    }
  );

  // mirror into local if you still need it
  useEffect(() => {
    if (isSuccess && resData) setData(resData);
  }, [isSuccess, resData]);

  return { data, isFetching, ...rest };
}
